import { createFileRoute, redirect, useNavigate, useRouter } from '@tanstack/react-router';
import { ProtectedRoute } from '../components/auth/index.js';
import { BdGraphPage } from '../components/features/basic-design/BdGraphPage.js';
import { canonicalBdKey } from './-bd-key.js';
import { parseBdGraphSearch } from './-bd-search.js';

/**
 * 基本設計の図だけのページ。基本設計のページの「新しいタブで開く」から開く。
 *
 * ファイル名の `$bdKey_` は基本設計のページ（`projects.bd.$bdKey.tsx`）の子にしないための印。
 * 子にすると親のページの中（Outlet を持たない）に描かれ、図だけのページにならない。
 *
 * @graph-node {Page}
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 基本設計の全体図・ER 図・画面遷移図を画面いっぱいに描くページ。開く図は URL の view で選び、小文字のチケット番号は大文字の URL へ送り直す
 * @graph-connects BdGraphPage [calls] 図だけのページの描画
 * @graph-connects canonicalBdKey [calls] チケット番号の正規化
 * @graph-connects parseBdGraphSearch [calls] 開く図の検証
 */
export const Route = createFileRoute('/projects/bd/$bdKey_/graph')({
  validateSearch: parseBdGraphSearch,
  beforeLoad: ({ params, search }) => {
    const canonical = canonicalBdKey(params.bdKey);
    if (canonical !== null) {
      throw redirect({
        to: '/projects/bd/$bdKey/graph',
        params: { bdKey: canonical },
        search,
        replace: true,
      }) as unknown as Error;
    }
  },
  component: BdGraphRoutePage,
});

/**
 * 図の切り替えは履歴に積まない（戻るで図を 1 枚ずつ戻ってもうれしくない）。
 * @graph-connects BdGraphPage [calls] 図だけのページの描画
 * @graph-connects canonicalBdKey [calls] チケット番号の正規化
 */
function BdGraphRoutePage() {
  const params = Route.useParams();
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const router = useRouter();
  const bdKey = canonicalBdKey(params.bdKey) ?? params.bdKey;

  return (
    <ProtectedRoute pageKey="basicDesign">
      <BdGraphPage
        bdKey={bdKey}
        view={search.view ?? 'overview'}
        onViewChange={(view) => {
          void navigate({ search: { view }, replace: true });
        }}
        sectionHref={(section) =>
          router.buildLocation({
            to: '/projects/bd/$bdKey',
            params: { bdKey },
            search: { section },
          }).href
        }
      />
    </ProtectedRoute>
  );
}
